class WellnessVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.wellness-video-carousel__viewport');
    this.track = this.querySelector('.wellness-video-carousel__track');
    this.prevBtn = this.querySelector('.wellness-video-carousel__nav-prev');
    this.nextBtn = this.querySelector('.wellness-video-carousel__nav-next');
    this.modal = this.querySelector('.wellness-video-carousel__modal');
    this.modalVideo = this.querySelector('.wellness-video-carousel__modal-video');
    this.closeBtn = this.querySelector('.wellness-video-carousel__modal-close');
    this.isAnimating = false;

    if (!this.viewport || !this.track) return;

    this.prevBtn?.addEventListener('click', () => this.navigate(-1));
    this.nextBtn?.addEventListener('click', () => this.navigate(1));

    this.viewport.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
          event.preventDefault();
        }
      },
      { passive: false }
    );

    this.querySelectorAll('[data-wellness-video-url]').forEach((btn) => {
      btn.addEventListener('click', () => this.openVideo(btn.dataset.wellnessVideoUrl));
    });

    this.closeBtn?.addEventListener('click', () => this.closeVideo());
    this.modal?.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeVideo();
    });
    this.modal?.addEventListener('cancel', () => this.closeVideo());

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isAnimating) return;
      this.centerTrack(false);
    });
    this.resizeObserver.observe(this.viewport);

    this.initStartingSlide();
    this.centerTrack(false);
    this.updateNavState();
  }

  initStartingSlide() {
    const slides = this.getSlides();
    if (!slides.length) return;

    const middleIndex = Math.floor((slides.length - 1) / 2);
    const middleSlide = slides[middleIndex];
    const active = this.getActiveSlide();

    if (active !== middleSlide) {
      this.setActiveSlide(middleSlide);
    }
  }

  getRootStyles() {
    return getComputedStyle(this.closest('.wellness-video-carousel'));
  }

  getGap() {
    const raw = this.getRootStyles().getPropertyValue('--wvc-slide-gap').trim();
    const value = parseFloat(raw);
    if (raw.endsWith('rem')) {
      return value * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
    }
    return value || 16;
  }

  getSlotWidth() {
    const raw = this.getRootStyles().getPropertyValue('--wvc-w-active').trim();
    if (!raw) return this.getSlides()[0]?.offsetWidth || 0;

    if (raw.endsWith('rem')) {
      const rem = parseFloat(raw);
      return rem * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
    }

    if (raw.startsWith('min(')) {
      const remMatch = raw.match(/([\d.]+)rem/);
      const vwMatch = raw.match(/([\d.]+)vw/);
      const remPx = remMatch
        ? parseFloat(remMatch[1]) * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16)
        : Infinity;
      const vwPx = vwMatch ? (parseFloat(vwMatch[1]) / 100) * window.innerWidth : Infinity;
      return Math.min(remPx, vwPx);
    }

    return parseFloat(raw) || 0;
  }

  getSlides() {
    return [...this.track.querySelectorAll('.wellness-video-carousel__slide')];
  }

  getSlideOuterWidth() {
    return this.getSlotWidth() + this.getGap();
  }

  getActiveSlide() {
    return this.track.querySelector('.wellness-video-carousel__slide--active') || this.getSlides()[0];
  }

  setActiveSlide(slide) {
    this.getSlides().forEach((item) => {
      item.classList.toggle('wellness-video-carousel__slide--active', item === slide);
    });
  }

  getCenterOffsetForSlide(slide) {
    if (!slide) return 0;

    const slides = this.getSlides();
    const viewportWidth = this.viewport.offsetWidth;
    const slotWidth = this.getSlotWidth();
    let index = 0;

    for (let i = 0; i < slides.length; i += 1) {
      if (slides[i] === slide) {
        index = i;
        break;
      }
    }

    const offset = index * this.getSlideOuterWidth();
    return offset + slotWidth / 2 - viewportWidth / 2;
  }

  getCenterOffset() {
    return this.getCenterOffsetForSlide(this.getActiveSlide());
  }

  centerTrack(animate) {
    const offset = this.getCenterOffset();
    this.track.style.transition = animate ? 'transform 450ms cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none';
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  updateNavState() {
    const slides = this.getSlides();
    const active = this.getActiveSlide();

    if (!active || slides.length < 2) {
      this.setNavDisabled(this.prevBtn, true);
      this.setNavDisabled(this.nextBtn, true);
      return;
    }

    const isFirst = active === slides[0];
    const isLast = active === slides[slides.length - 1];

    this.setNavDisabled(this.prevBtn, isFirst);
    this.setNavDisabled(this.nextBtn, isLast);
  }

  setNavDisabled(button, disabled) {
    if (!button) return;

    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    button.classList.toggle('wellness-video-carousel__nav-btn--disabled', disabled);
  }

  finishTransition(callback) {
    let done = false;
    const complete = () => {
      if (done) return;
      done = true;
      callback();
    };

    const onEnd = (event) => {
      if (event.propertyName !== 'transform' || event.target !== this.track) return;
      this.track.removeEventListener('transitionend', onEnd);
      complete();
    };

    this.track.addEventListener('transitionend', onEnd);
    window.setTimeout(complete, 500);
  }

  navigate(direction) {
    if (this.isAnimating) return;

    const slides = this.getSlides();
    if (slides.length < 2) return;

    const active = this.getActiveSlide();
    if (!active) return;

    const target = direction > 0 ? active.nextElementSibling : active.previousElementSibling;
    if (!target) return;

    this.isAnimating = true;

    const targetOffset = this.getCenterOffsetForSlide(target);
    this.track.style.transition = 'transform 450ms cubic-bezier(0.25, 0.1, 0.25, 1)';
    this.track.style.transform = `translate3d(${-targetOffset}px, 0, 0)`;

    this.finishTransition(() => {
      this.setActiveSlide(target);
      this.track.style.transition = 'none';
      this.centerTrack(false);
      this.updateNavState();
      this.isAnimating = false;
    });
  }

  openVideo(url) {
    if (!this.modal || !this.modalVideo || !url) return;

    this.modalVideo.src = url;
    this.modalVideo.currentTime = 0;
    this.modal.showModal();
    this.modalVideo.play().catch(() => {});
  }

  closeVideo() {
    if (!this.modal || !this.modalVideo) return;

    this.modalVideo.pause();
    this.modalVideo.removeAttribute('src');
    this.modalVideo.load();
    this.modal.close();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('wellness-video-carousel')) {
  customElements.define('wellness-video-carousel', WellnessVideoCarousel);
}
